/* eslint-disable no-console */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import api from 'api';
import { JobFolder } from 'components/Library/types';
import { useSignalRState } from 'contexts/SignalRContext';
import { useLocation } from 'react-router-dom';
import {
   selectImageView,
   setCoatThickness,
   setCoatType,
   setDecodedImageData,
   stateOfGeneralSlice,
} from 'state/general/general.slice';
import { CoatThickness, CoatType, JOB_STATUS } from 'state/general/general.types';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { convertBinaryToImage } from 'utils';
import { getImageStaticUrls } from 'utils/index'; // Correct the error by importing the function
import { parseSharedPreviewQuery } from 'utils/sharedPreviewLink';

const authToken = process.env.REACT_APP_AUTHORIZED_TOKEN;

export { legacySharedPreviewAllowsEdit as sharedPreviewUrlAllowsEdit } from 'utils/sharedPreviewLink';

export const usePreviewLogic = () => {
   const dispatch = useAppDispatch();
   const location = useLocation();
   const isSharedPreviewRoute = location.pathname.startsWith('/preview/shared');

   const sharedQuery = useMemo(() => {
      if (!isSharedPreviewRoute) return null;
      return parseSharedPreviewQuery(new URLSearchParams(location.search));
   }, [isSharedPreviewRoute, location.search]);

   const sharedParsed = sharedQuery?.ok ? sharedQuery.data : null;
   const sharedFolder = sharedParsed?.folderId ?? '';
   const hasSharedParams = Boolean(sharedParsed);
   const sharedCoatType = (sharedParsed?.coatType ?? '') as CoatType;
   const sharedThickness = (sharedParsed?.thickness ?? '') as CoatThickness;
   const sharedLayer = sharedParsed?.layer ?? '';
   const sharedAllowsEdit = sharedParsed?.allowEdit ?? true;

   const { settings, selectedImageView, decodedThreeJSData, isLibraryVisible } =
      useAppSelector(stateOfGeneralSlice);
   const { jobStatus, folderName } = settings;

   const { filesReady, clearProgress } = useSignalRState();

   // Add state for expiration message
   const [expirationMessage, setExpirationMessage] = useState<string>('');

   // Track last loaded layer URI to prevent infinite loops
   const lastLoadedLayerUriRef = useRef<string | null>(null);
   // Use ref to access decodedThreeJSData without causing dependency issues
   const decodedThreeJSDataRef = useRef(decodedThreeJSData);

   useEffect(() => {
      decodedThreeJSDataRef.current = decodedThreeJSData;
   }, [decodedThreeJSData]);

   useEffect(() => {
      if (!isSharedPreviewRoute || !sharedQuery) return;
      if (!sharedQuery.ok) {
         setExpirationMessage(
            sharedQuery.reason === 'invalid_token'
               ? 'Invalid or corrupted shared preview link.'
               : 'This shared preview link is missing required data.',
         );
      }
   }, [isSharedPreviewRoute, sharedQuery]);

   const fetchAsObjectURL = async (url: string): Promise<string> => {
      console.log('********** 3', url);
      const res = await fetch(url, { mode: 'cors' });
      console.log('********** 4', res);
      if (!res.ok) throw new Error(`Failed to fetch ${url}: ${res.status}`);
      const blob = await res.blob();
      return URL.createObjectURL(blob);
   };

   const loadImagesFromUrls = useCallback(
      async (printColorUrl: string, printBWUrl: string) => {
         try {
            console.log('********** 2');

            const currentData = decodedThreeJSDataRef.current;
            if (currentData?.printColor && currentData.printColor.startsWith('blob:')) {
               try {
                  URL.revokeObjectURL(currentData.printColor);
               } catch (e) {
                  // Ignore errors when revoking URLs
               }
            }
            if (currentData?.printBW && currentData.printBW.startsWith('blob:')) {
               try {
                  URL.revokeObjectURL(currentData.printBW);
               } catch (e) {
                  // Ignore errors when revoking URLs
               }
            }

            const [colorBlobUrl, bwBlobUrl] = await Promise.all([
               fetchAsObjectURL(printColorUrl),
               fetchAsObjectURL(printBWUrl),
            ]);

            dispatch(
               setDecodedImageData({
                  printColor: colorBlobUrl,
                  printBW: bwBlobUrl,
               }),
            );

            // Set default view if not already set
            if (!selectedImageView) {
               dispatch(selectImageView('Preview'));
            }
         } catch (e) {
            console.error('Error loading remote images:', e);
         }
      },
      [selectedImageView, dispatch],
   );

   // Fetch files from your API
   const fetchFilesFromApi = async (folderId: string, layer?: string) => {
      try {
         setExpirationMessage(''); // Clear any previous messages

         const response = await fetch(
            `${process.env.REACT_APP_SCODIX_API_URL}/visualization/get-files/${folderId}/${process.env.REACT_APP_DEFAULT_MAX_DAYS}`,
            {
               headers: {
                  Authorization: `Bearer ${authToken}`,
                  'Content-Type': 'application/json',
               },
            },
         );

         const data = await response.json();

         if (data.success) {
            // Check if files are expired
            if (data.expired) {
               console.log('********** data.Message', data.Message);
               setExpirationMessage(
                  data.Message || 'The preview files have expired and are no longer available.',
               );

               // Clear any existing images
               dispatch(
                  setDecodedImageData({
                     printColor: '',
                     printBW: '',
                  }),
               );

               return;
            }

            // If we have files array, proceed to load them
            if (data.files && data.files.length >= 2) {
               // Find _c.webp and _s.webp files
               const colorFile = data.files.find((url: string) => url.includes('_c.webp'));
               // For bwFile, search for files matching pattern: {fileName}_{selectedLayer}_s
               // If layer is provided, search for that layer, otherwise find any _s.webp file
               const bwFile = layer
                  ? data.files.find((url: string) => url.includes(`_${layer}_s`) && url.includes('_s.webp'))
                  : data.files.find((url: string) => url.includes('_s.webp'));

               if (colorFile && bwFile) {
                  await loadImagesFromUrls(colorFile, bwFile);
               } else {
                  console.error('Could not find required files in API response');
                  setExpirationMessage('Required preview files not found.');
               }
            } else {
               console.error('No files found in API response');
               setExpirationMessage('No preview files available.');
            }
         } else {
            console.error('API response not successful:', data);
            setExpirationMessage('Failed to load preview files.');
         }
      } catch (error) {
         console.error('Error fetching files from API:', error);
         setExpirationMessage('Error connecting to preview service.');
      }
   };

   // If shared preview link params exist, load from API
   useEffect(() => {
      if (!hasSharedParams) return;

      // Use the folder ID from URL params to call your API
      // Pass sharedLayer if available to filter the correct bwFile
      void fetchFilesFromApi(sharedFolder, sharedLayer || undefined);

      // Apply shared coating parameters if provided
      const validCoatTypes: CoatType[] = [
         'varnish',
         'gold',
         'silver',
         'silverBlue',
         'bronze',
         'roseGold',
         'champagne',
         'emeraldGreen',
         'burgundy',
         'lavender',
         'royalPurple',
         'black',
      ];
      const validThickness: CoatThickness[] = ['min', 'mid', 'max'];

      if (validCoatTypes.includes(sharedCoatType)) {
         dispatch(setCoatType(sharedCoatType));
         const coatTypeToImageView: Record<CoatType, 'Preview' | 'sense' | 'silver foil'> = {
            varnish: 'sense',
            gold: 'Preview',
            silver: 'silver foil',
            silverBlue: 'Preview',
            bronze: 'Preview',
            roseGold: 'Preview',
            champagne: 'Preview',
            emeraldGreen: 'Preview',
            burgundy: 'Preview',
            lavender: 'Preview',
            royalPurple: 'Preview',
            black: 'Preview',
         };
         dispatch(selectImageView(coatTypeToImageView[sharedCoatType]));
      } else {
         dispatch(selectImageView('Preview'));
      }

      if (validThickness.includes(sharedThickness)) {
         dispatch(setCoatThickness(sharedThickness));
      }
   }, [hasSharedParams, sharedFolder, sharedLayer, sharedCoatType, sharedThickness, dispatch]);

   // Helper function to load images with selected layer
   const loadImagesWithLayer = useCallback(
      (layerName: string | null = null) => {
         if (!filesReady || hasSharedParams) return;

         const layers = filesReady.scodixFileData ?? [];
         const selectedLayer =
            layerName ||
            (() => {
               try {
                  return sessionStorage.getItem('previewLayer');
               } catch (e) {
                  return null;
               }
            })();

         const matchedLayer = selectedLayer ? layers.find((x) => x.Layer === selectedLayer) : layers[0];

         if (!matchedLayer) {
            console.warn('No matching Scodix layer found for preview');
            return;
         }

         // Prevent reloading the same layer URI
         const layerUriKey = `${filesReady.uriCMYK}|${matchedLayer.Uri}`;
         if (lastLoadedLayerUriRef.current === layerUriKey) {
            return; // Already loading/loaded this layer combination
         }

         lastLoadedLayerUriRef.current = layerUriKey;
         clearProgress();
         void loadImagesFromUrls(filesReady.uriCMYK, matchedLayer.Uri);
      },
      [filesReady, hasSharedParams, clearProgress, loadImagesFromUrls],
   );

   // Reset ref when filesReady changes
   useEffect(() => {
      if (filesReady) {
         lastLoadedLayerUriRef.current = null;
      }
   }, [filesReady]);

   // Handle SignalR FilesReady event
   useEffect(() => {
      if (hasSharedParams) return;

      if (filesReady) {
         console.log('SignalR FilesReady received:', filesReady);
         loadImagesWithLayer();
      }
   }, [filesReady, hasSharedParams, loadImagesWithLayer]);

   // Handle layer change event from AppBar dropdown
   useEffect(() => {
      if (hasSharedParams || !filesReady) return;

      const handleLayerChange = (event: CustomEvent<{ layer: string }>) => {
         const newLayer = event.detail?.layer;
         if (newLayer) {
            loadImagesWithLayer(newLayer);
         }
      };

      window.addEventListener('preview-layer-changed', handleLayerChange as EventListener);
      return () => {
         window.removeEventListener('preview-layer-changed', handleLayerChange as EventListener);
      };
   }, [filesReady, hasSharedParams, loadImagesWithLayer]);

   // Function to handle cell clicks
   const handleLibraryCellClick = (job: JobFolder) => {
      console.log('job', job);
      const setImageUrls = (basePath: string) => {
         console.log('basePath', basePath);
         console.log('********** 5');
         dispatch(
            setDecodedImageData({
               printColor: `${basePath}/Cmyk.webp`,
               printBW: `${basePath}/Merge_results.webp`,
            }),
         );
      };

      switch (job) {
         case JobFolder.IMAGE:
            {
               // Try configured URLs from localStorage first; fallback to env
               const { colorImageUrl: colorUrl, bwImageUrl: bwUrl } = getImageStaticUrls();
               if (colorUrl && bwUrl) {
                  void loadImagesFromUrls(colorUrl, bwUrl);
               } else {
                  console.warn('Missing IMAGE URLs. Set localStorage image_color_url and image_bw_url');
               }
               break;
            }
            console.log('********** 9');
         case JobFolder.KOA:
            setImageUrls('env/Koa');
            break;
         case JobFolder.CARD_3:
            setImageUrls('env/Card3');
            break;
         case JobFolder.INFINITY_BOX:
            setImageUrls('env/Infinity_Box');
            break;
         case JobFolder.LOVE_CARD:
            setImageUrls('env/Love_Card');
            break;
         case JobFolder.ICELAND_BOOK:
            setImageUrls('env/Scodix_ICELAND_Book');
            break;
         case JobFolder.SCODIX_PROVEN:
            setImageUrls('env/Scodix_Proven');
            break;
         case JobFolder.THANK_YOU_CARD:
            setImageUrls('env/Thank_You_Card');
            break;
         case JobFolder.STOLEN_BANKER_BOOK:
            setImageUrls('env/Stolen_Banker');
            break;
         case JobFolder.WEDDING_1:
            setImageUrls('env/Wedding_1');
            break;
         case JobFolder.WEDDING_2:
            setImageUrls('env/Wedding_2');
            break;
         case JobFolder.WEDDING_3:
            setImageUrls('env/Wedding_3');
            break;
         case JobFolder.WEDDING_4:
            setImageUrls('env/Wedding_4');
            break;
         default:
            break;
      }
   };

   const showToolPanel = !isSharedPreviewRoute || sharedAllowsEdit;

   return {
      selectedImageView,
      decodedThreeJSData,
      isLibraryVisible,
      expirationMessage,
      showToolPanel,
      handlers: {
         handleLibraryCellClick,
         loadImagesFromUrls,
      },
   };
};
